/**
 * Dialog
 * 对Portal的封装，自带mask相关样式
 * 包含出现消失相关的动画处理
 *
 * Created by Shen Mo on 2022-5-9
 */
import cx from 'clsx';
import { Children, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion, motionify } from '@motion';
import { Portal } from '@core/component/portal/Portal';
import { useControllableState } from '@core/hooks/useControllableState';
import useEvent from '@core/hooks/useEvent';

const DEFAULT_ANIMATION = {
	initial: {
		y: 20,
		opacity: 0,
		scale: 0.82,
	},
	animate: {
		y: 0,
		scale: 1,
		opacity: 1,
		transition: { type: 'spring', stiffness: 400, damping: 40 },
	},
	exit: {
		y: 10,
		scale: 0.97,
		opacity: 0,
		transition: { type: 'spring', stiffness: 320, damping: 30 },
	},
};
const MASK_ANIMATION = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	exit: { opacity: 0, transition: { duration: 0.35 } },
};

const useMotionAnimation = ({ initial, animate, exit, variants }, defaultAnimation) => {
	return useMemo(() => {
		// 一种标记没有动画的方式
		if (initial === null && animate === null && exit === null && variants === null) {
			return null;
		} else {
			return {
				initial: initial !== undefined ? initial : defaultAnimation.initial,
				animate: animate !== undefined ? animate : defaultAnimation.animate,
				exit: exit !== undefined ? exit : defaultAnimation.exit,
			};
		}
	}, [initial, animate, exit, variants]);
};
export const Dialog = forwardRef(function Dialog(props, ref) {
	const {
		defaultVisible,
		visible: dialogVisible,
		className,
		// 默认支持点击外部关闭
		closeClickAway = true,
		closeEsc = true,
		// 动画相关接口暴露
		initial,
		animate,
		exit,
		variants,
		onClose,
		onVisibleChange,
		onExitComplete,
		children,
	} = props;

	const [presence, setPresence] = useState(true);
	// 缓存 motion.Component - 避免重复创建导致动画重复执行
	const prevChildren = useRef(null);
	const animationProps = useMotionAnimation({ initial, animate, exit, variants }, DEFAULT_ANIMATION);

	const [visible, setVisible] = useControllableState({
		prop: dialogVisible,
		defaultProp: defaultVisible,
		onChange: onVisibleChange,
	});

	useImperativeHandle(ref, () => {
		return {
			isOpen() {
				return visible;
			},
			open() {
				handleOpen();
			},
			close() {
				handleClose();
			},
		};
	});

	useEffect(() => {
		if (visible) {
			setPresence(true);
		}
	}, [visible]);


	const handleOpen = useEvent(() => {
		setVisible(true);
	});
	const handleClose = useEvent(() => {
		setVisible(false);
		onClose?.();
	});

	const handleAnimationComplete = useCallback(() => {
		setPresence(() => false);
		onExitComplete && onExitComplete();
	}, [onExitComplete]);

	// convert component to motion component
	const content = useMemo(() => {
		let content = children;
		if (Children.only(children) && animationProps) {
			let MotionChild;
			if (!prevChildren.current || children.type !== prevChildren.current.type) {
				MotionChild = motionify(children.type);
				prevChildren.current = {
					type: children.type,
					component: MotionChild,
				};
			} else {
				MotionChild = prevChildren.current.component;
			}

			const { style, ...rest } = children.props;
			content = <MotionChild key={'dialog-content'} {...animationProps} {...rest} ref={children.ref} style={style} />;
		}
		return content;
	}, [children]);

	return (
		<Portal
			className={cx(`mg-dialog`, className)}
			visible={visible || presence}
			closeClickAway={closeClickAway}
			closeEsc={closeEsc}
			onClose={handleClose}>
			<AnimatePresence onExitComplete={handleAnimationComplete}>
				{visible ? (
					<motion.div
						className={`mg-dialog-mask`}
						variants={MASK_ANIMATION}
						initial={'initial'}
						animate={'animate'}
						exit={'exit'}
					/>
				) : null}
				{visible ? content : null}
			</AnimatePresence>
		</Portal>
	);
});
